<script setup>
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import { gsap } from 'gsap'
  import FlyoutMenuSmallItems from '@/components/Ui/FlyoutMenuSmallItems.vue'
  import { useAccessibility } from '@/composables/accessibility'

  const activeIndex = ref(0)
  const closeButton = ref(null)
  const { handleFlyoutMenuKeyNavigation } = useAccessibility()

  const props = defineProps({
    items: Object,
    smallItems: Object,
    open: Boolean
  })

  const subItems = computed(() => {
    if (activeIndex.value === undefined) {
      return props.items[0].children
    }
    return props.items[activeIndex.value].children
  })

  onMounted(async () => {
    triggerAnimateIn()
    closeButton.value = document.querySelector('.flyout-menu-close-button')

    closeButton.value.addEventListener('click', () => {
      triggerAnimateOut()
    })
  })

  const showAsColumns = computed(() => {
    // if the subItems computed hast more than 5 items. Andi if the items dont have further children
    if (subItems.value.length < 5 && !subItems.value.some(item => item.children)) {
      return false
    }
    return true
  })

  const triggerAnimateIn = () => {
    gsap.from('.menu-item-top', {
      opacity: 0,
      y: 20,
      duration: 1,
      stagger: 0.4
    })
    gsap.from('.flyout-menu-subitems', {
      opacity: 0,
      duration: 1,
      delay: 1
    })
  }

  const triggerAnimateOut = () => {
    gsap.to('.menu-item-top', {
      opacity: 0,
      y: 20,
      duration: 1,
      stagger: 0.4
    })
    gsap.to('.flyout-menu-subitems', {
      opacity: 0,
      duration: 1,
    })
  }

</script>

<template>
  <div
    class="flyout-menu-items-top relative w-full flex flex-col border-r border-white/30 h-full py-14 2xl:py-32"
  >
    <button
      v-for="(item, index) in items"
      :key="index"
      :class="activeIndex === index ? 'active' : ''"
      @mouseenter="activeIndex = index"
       @focus="activeIndex = index"
       @keydown="handleFlyoutMenuKeyNavigation"
      class="menu-item-top font-light first:border-t flex flex-row items-center border-b !border-white/30 hover:!border-b-white cursor-pointer transition-colors text-white py-8 ~text-xl/4xl hover:opacity-100"
    >
      <span
        class="!bg-transparent"
      >
        <AppIcon
          name="ArrowLink"
        />
      </span>
      {{ item.title }}
    </button>
    <FlyoutMenuSmallItems
      :items="smallItems"
    />
  </div>
  <div
    class="flyout-menu-subitems col-span-2 grid gap-x-4 gap-y-8 content-baseline naviBig:pt-36 px-32"
    :class="showAsColumns ? 'grid-cols-2' : 'grid-cols-1'"
    v-if="subItems"
  >
    <div
      v-for="(item, index) in subItems"
      :key="index"
      class="menu-subitem-inner"
    >
      <a
        @keydown="handleFlyoutMenuKeyNavigation"
        :href="item.url"
        class="menu-subitem relative pb-6 2xl:pb-8 text-base 2xl:text-xl !text-white"
        :class="item.children ? 'has-children' : ''"
      >
        {{ item.title }}
      </a>
      <div
        v-if="item.children"
        class="flyout-menu-subitems-children flex flex-col mt-6"
      >
        <a
          v-for="(subItem, index) in item.children"
           :key="index"
           @keydown="handleFlyoutMenuKeyNavigation"
          :href="subItem.url"
          class="menu-subitem font-light transition-colors pb-6 2xl:pb-8 text-base 2xl:text-xl !text-white relative"
        >
          {{ subItem.title }}
        </a>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  :deep(.menu-item-top svg) {
    @apply mr-3 max-w-0 transition-all duration-300 ease-in-out;
  }
  :deep(.menu-item-top svg path) {
    fill: #fff !important;
  }
  :deep(.menu-item-top.active svg) {
    @apply max-w-8;
  }

  .flyout-menu-subitems {
    a {
      @apply font-light transition-colors !text-white;
      &.has-children {
        @apply bg-white !text-primary px-6 py-2 rounded-full lg:-ml-6;
      }
    }
  }

</style>
